/* Renact cart — drawer toggle + AJAX line item updates.
   Uses Shopify's Cart API (/cart/change.js, /cart/update.js, /cart.js)
   and the Section Rendering API (?sections=renact-cart-drawer) to
   refresh the drawer + cart-page line-items DOM after each change.
*/
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var DRAWER_SELECTOR = '[data-renact-cart-drawer]';
  var DRAWER_SECTION_ID = 'renact-cart-drawer';
  var PAGE_SECTION_ID = null; // resolved at runtime if a cart page is on screen

  // ───────────────────────────────────────────────────────────────────────
  // Drawer open/close
  // ───────────────────────────────────────────────────────────────────────

  var lastFocused = null;

  function getDrawer() { return document.querySelector(DRAWER_SELECTOR); }

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.setAttribute('data-state', 'open');
    drawer.removeAttribute('aria-hidden');
    document.documentElement.style.overflow = 'hidden';
    var closeBtn = drawer.querySelector('.renact-cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.setAttribute('data-state', 'closed');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  // Minimal public API so other sections (e.g. the product page, which has its
  // own add-to-cart form) can re-render + open the drawer through the same code
  // path as the collection cards — instead of only updating the header count.
  window.RenactCart = {
    refresh: function (cart) { return refreshAfterChange(cart); },
    open: openDrawer,
    close: closeDrawer,
    refreshAndOpen: function (cart) {
      return Promise.resolve(refreshAfterChange(cart)).then(function () { openDrawer(); });
    }
  };

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-renact-cart-toggle]');
    if (toggle) {
      // Open drawer instead of navigating to /cart, but only when:
      //  - cart_type setting is 'drawer' (we infer this from the drawer being present)
      //  - no modifier keys (so cmd+click still opens /cart in a new tab)
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var drawer = getDrawer();
      if (!drawer) return;
      event.preventDefault();
      openDrawer();
      // Always re-sync the drawer from the live cart on open, so it can never
      // show stale/empty contents if a prior add's refresh was missed.
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { return refreshAfterChange(cart); })
        .catch(function () {});
      return;
    }
    var close = event.target.closest('[data-renact-cart-close]');
    if (close) {
      event.preventDefault();
      closeDrawer();
      return;
    }
    var closeOnClick = event.target.closest('[data-renact-cart-close-on-click]');
    if (closeOnClick) {
      // Anchor to a link — let the navigation happen, but also close the drawer.
      closeDrawer();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      var drawer = getDrawer();
      if (drawer && drawer.getAttribute('data-state') === 'open') {
        closeDrawer();
      }
    }
  });

  // ───────────────────────────────────────────────────────────────────────
  // AJAX add-to-cart — delegated submit handler for ANY [data-renact-card-add]
  // form on the page. Avoids per-section duplication, survives section JS
  // errors, and chains into the drawer refresh + open after success.
  // ───────────────────────────────────────────────────────────────────────

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-renact-card-add]');
    if (!form) return;
    event.preventDefault();

    var btn = form.querySelector('button[type="submit"]');
    if (btn && btn.disabled) return;

    var labelDefault = form.querySelector('[data-renact-add-default]');
    var labelLoading = form.querySelector('[data-renact-add-loading]');
    var labelSuccess = form.querySelector('[data-renact-add-success]');

    // Loading UI
    if (btn) btn.setAttribute('disabled', '');
    if (labelDefault) labelDefault.setAttribute('hidden', '');
    if (labelLoading) labelLoading.removeAttribute('hidden');
    if (labelSuccess) labelSuccess.setAttribute('hidden', '');

    fetch(form.action || '/cart/add', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error((res.body && res.body.description) || 'Cart error');

        // Success UI
        if (labelLoading) labelLoading.setAttribute('hidden', '');
        if (labelSuccess) labelSuccess.removeAttribute('hidden');
        if (btn) btn.classList.add('is-success');

        // Pull the latest cart, re-render drawer + open it
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { return refreshAfterChange(cart); })
          .then(function () { openDrawer(); })
          .catch(function () { openDrawer(); })
          .finally(function () {
            // Revert button after a beat so the user sees the ✓ state
            setTimeout(function () {
              if (btn) {
                btn.classList.remove('is-success');
                btn.removeAttribute('disabled');
              }
              if (labelSuccess) labelSuccess.setAttribute('hidden', '');
              if (labelDefault) labelDefault.removeAttribute('hidden');
            }, 1600);
          });
      })
      .catch(function (err) {
        console.warn('[renact-cart] add failed', err);
        if (labelLoading) labelLoading.setAttribute('hidden', '');
        if (labelDefault) labelDefault.removeAttribute('hidden');
        if (btn) btn.removeAttribute('disabled');
      });
  });

  // ───────────────────────────────────────────────────────────────────────
  // Line item qty / remove
  // ───────────────────────────────────────────────────────────────────────

  // Debounce so the user can rapid-fire +/- without flooding the cart API.
  var pending = new Map();
  function scheduleChange(key, qty) {
    if (pending.has(key)) clearTimeout(pending.get(key));
    pending.set(key, setTimeout(function () {
      pending.delete(key);
      changeLine(key, qty);
    }, 250));
  }

  function changeLine(key, qty) {
    setBusy(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (res) { return res.json().then(function (b) { return { ok: res.ok, body: b }; }); })
      .then(function (result) {
        if (!result.ok) {
          throw new Error((result.body && result.body.description) || 'Cart update failed');
        }
        return refreshAfterChange(result.body);
      })
      .catch(function (err) {
        console.error('[renact-cart]', err);
      })
      .finally(function () { setBusy(false); });
  }

  function setBusy(state) {
    var drawer = getDrawer();
    if (drawer) drawer.toggleAttribute('data-busy', state);
    document.querySelectorAll('[data-renact-cart-page]').forEach(function (el) {
      el.toggleAttribute('data-busy', state);
    });
  }

  // ───────────────────────────────────────────────────────────────────────
  // Refresh DOM via Section Rendering API
  // ───────────────────────────────────────────────────────────────────────

  function refreshAfterChange(cart) {
    // Broadcast for the header counter, etc.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));

    var sections = [DRAWER_SECTION_ID];
    var pageSectionId = resolvePageSectionId();
    if (pageSectionId) sections.push(pageSectionId);

    return fetch('/?sections=' + sections.join(','), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (rendered) {
        // Drawer
        if (rendered[DRAWER_SECTION_ID]) {
          replaceDrawer(rendered[DRAWER_SECTION_ID]);
        }
        // Cart page
        if (pageSectionId && rendered[pageSectionId]) {
          replacePage(rendered[pageSectionId]);
        }
        // Update header subtotal/count fallbacks (in case cart:updated listener
        // doesn't catch a particular bit of UI)
        updateInlineTotals(cart);
      })
      .catch(function (err) {
        console.warn('[renact-cart] section render failed; falling back to inline updates', err);
        updateInlineTotals(cart);
      });
  }

  function resolvePageSectionId() {
    if (PAGE_SECTION_ID) return PAGE_SECTION_ID;
    var pageEl = document.querySelector('[data-renact-cart-page]');
    if (!pageEl) return null;
    var sectionEl = pageEl.closest('.shopify-section');
    if (!sectionEl) return null;
    var id = sectionEl.id || '';
    // Shopify section ids look like "shopify-section-template--XXXX__main"
    var match = id.match(/shopify-section-(.+)$/);
    PAGE_SECTION_ID = match ? match[1] : null;
    return PAGE_SECTION_ID;
  }

  function replaceDrawer(html) {
    var drawer = getDrawer();
    if (!drawer) return;
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    var newDrawer = wrapper.querySelector(DRAWER_SELECTOR);
    if (!newDrawer) return;
    // Preserve the open state across the swap
    var wasOpen = drawer.getAttribute('data-state') === 'open';
    drawer.replaceWith(newDrawer);
    if (wasOpen) {
      newDrawer.setAttribute('data-state', 'open');
      newDrawer.removeAttribute('aria-hidden');
    }
  }

  function replacePage(html) {
    var current = document.querySelector('[data-renact-cart-page]');
    if (!current) return;
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();
    var fresh = wrapper.querySelector('[data-renact-cart-page]');
    if (!fresh) return;
    current.replaceWith(fresh);
  }

  function updateInlineTotals(cart) {
    if (!cart) return;
    document.querySelectorAll('[data-renact-cart-subtotal]').forEach(function (el) {
      el.textContent = formatMoney(cart.total_price);
    });
    document.querySelectorAll('[data-renact-cart-count-text]').forEach(function (el) {
      el.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
    });
  }

  // Bare-bones money formatter. Shopify themes typically have a richer
  // utility — for now we return a string that includes the cart's currency
  // symbol. Section Rendering API responses give the canonical formatted
  // money strings, so this is a fallback only.
  function formatMoney(cents) {
    if (cents == null) return '';
    var amount = (cents / 100).toFixed(2);
    return amount + ' ' + (window.Shopify && window.Shopify.currency && window.Shopify.currency.active || '');
  }

  // ───────────────────────────────────────────────────────────────────────
  // Wire up qty / remove inside any line-item (drawer or page)
  // ───────────────────────────────────────────────────────────────────────

  document.addEventListener('click', function (event) {
    var line = event.target.closest('[data-renact-cart-line]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    if (!key) return;

    if (event.target.closest('[data-renact-line-remove]')) {
      event.preventDefault();
      changeLine(key, 0);
      return;
    }

    var input = line.querySelector('[data-renact-line-qty-input]');
    if (!input) return;
    var current = parseInt(input.value, 10) || 1;

    if (event.target.closest('[data-renact-line-decr]')) {
      event.preventDefault();
      var next = Math.max(0, current - 1);
      input.value = next;
      scheduleChange(key, next);
    } else if (event.target.closest('[data-renact-line-incr]')) {
      event.preventDefault();
      var nextUp = current + 1;
      input.value = nextUp;
      scheduleChange(key, nextUp);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-renact-line-qty-input]');
    if (!input) return;
    var line = input.closest('[data-renact-cart-line]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var qty = parseInt(input.value, 10);
    if (Number.isNaN(qty) || qty < 0) qty = 0;
    input.value = qty;
    scheduleChange(key, qty);
  });

  // Persist cart note (cart page only) via /cart/update.js when blurred
  document.addEventListener('blur', function (event) {
    var note = event.target.closest('[data-renact-cart-note]');
    if (!note) return;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: note.value || '' })
    }).catch(function (err) { console.warn('[renact-cart] note update failed', err); });
  }, true);
})();
